
import { FC, useState, useEffect } from 'react'
import { useAuth } from '../../auth'
import { toast } from 'react-toastify'
import {
  getVesselsByCompany,
  getVesselById,
  getCompanyGroupAdmins,
  getSubCompanyAdmins,
  getAllInventoryCategoriesForCompany,
  createInventory,
  getSubComponents,
  getInventoryItemHeads,
  getInventoryItemSubHeads,
  getEquipmentList,
  getSubComponentsByEquipment,
  getPartsBySubcomponent,
  createGeneralInventoryItem,
  getGeneralInventoryItems,
} from '../core/_requests'
import {
  CompanyGroupAdmin,
  SubCompanyAdmin,
  Vessel,
  InventoryCategory,
  Part,
  CreateInventoryRequest,
  SubcatalogueType,
  UnitOfMeasure,
  SubComponent,
  InventoryItemHead,
  InventoryItemSubHead,
  InventoryItem,
  Equipment,
} from '../core/_models'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryUiScope } from '../core/procurementLibraryUiScope'
import { procurementTenantCgaId } from '../core/procurementTenantContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface UpdateInventoryModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  vesselId: number
  categories: SubcatalogueType[]
  parts: Part[]
}

const toNumber = (val: string) => {
  const num = parseInt(val, 10)
  return Number.isNaN(num) ? null : num
}

const GENERAL_UNIT_OPTIONS = ['PCS', 'SET', 'LTR', 'KG', 'MTR', 'BOX', 'PKT', 'PAIR']


const UpdateInventoryModal: FC<UpdateInventoryModalProps> = ({ visible, onClose, onSubmit, vesselId, categories, parts }) => {
  const { currentUser } = useAuth()
  const { getScope } = useProcurementPermissions()
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_SUBCATEGORY_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBCATEGORY_VIEW,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const libraryScopeCompany = libraryScope === 'COMPANY'
  const tenantCgaId = procurementTenantCgaId(currentUser)
  const effectiveCgaId = libraryScopeCompany ? tenantCgaId : undefined

  const [formData, setFormData] = useState({
    company: '',
    subCompany: '',
    vessel: '', // Add vessel field
    itemFor: 'TECHNICAL',
    category: '',
    head: '',
    subHead: '',
    machinery: '',
    part: '',
    subComponent: '',
    itemName: '',
    partNumber: '',
    code: '',
    description: '',
    unit: '',
    currentQuantity: '',
    reorderLevel: '',
    minThreshold: '',
    maxThreshold: '',
    unitCost: '',
    location: '',
    expiryDate: '',
    serialNumber: ''
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // API data states
  const [inventoryCategories, setInventoryCategories] = useState<InventoryCategory[]>([])
  const [heads, setHeads] = useState<InventoryItemHead[]>([])
  const [subHeads, setSubHeads] = useState<InventoryItemSubHead[]>([])
  const [filteredParts, setFilteredParts] = useState<Part[]>([])
  const [subComponents, setSubComponents] = useState<SubComponent[]>([])
  const [equipmentList, setEquipmentList] = useState<Equipment[]>([])
  const [filteredSubComponents, setFilteredSubComponents] = useState<SubComponent[]>([])
  const [isLoadingData, setIsLoadingData] = useState(false)

  // New state for dropdown data
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [subCompanies, setSubCompanies] = useState<SubCompanyAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)
  const [unitsOfMeasure, setUnitsOfMeasure] = useState<UnitOfMeasure[]>([])
  const machineryOptions = equipmentList
  const isGeneralItem = formData.itemFor === 'GENERAL_STORES'
  const [generalItems, setGeneralItems] = useState<InventoryItem[]>([])
  const [selectedGeneralItemId, setSelectedGeneralItemId] = useState<string>('')
  const [isLoadingGeneralItems, setIsLoadingGeneralItems] = useState(false)
  const isGeneralItemExisting = Boolean(selectedGeneralItemId && selectedGeneralItemId !== 'OTHER')

  const filterVesselsForContext = (list: Vessel[]) => {
    if (vesselId) {
      return list.filter(v => v.id === vesselId)
    }
    return list
  }

  // Load initial data when modal opens
  useEffect(() => {
    if (visible) {
      loadInitialModalData()
      if (libraryScopeAll) {
        loadCompanies()
      } else {
        loadVesselsForCurrentUser()
      }
    }
  }, [visible, libraryScopeAll])

  // Load vessels for current user (non-superadmin)
  const loadVesselsForCurrentUser = async () => {
    try {
      // Get the current user's company username
      const companyUsername = (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
      if (companyUsername) {
        console.log('UpdateInventoryModal - Loading vessels for current user:', companyUsername)
        const vesselsData = await getVesselsByCompany(companyUsername)
        console.log('UpdateInventoryModal - Vessels loaded for current user:', vesselsData)
        const scoped = filterVesselsForContext(Array.isArray(vesselsData) ? vesselsData : [])
        setVessels(scoped)
      }
    } catch (error) {
      console.error('UpdateInventoryModal - Error loading vessels for current user:', error)
      setVessels([])
    }
  }

  // Load subcompanies when company is selected
  useEffect(() => {
    if (formData.company) {
      loadSubCompanies(parseInt(formData.company))
    } else {
      setSubCompanies([])
    }
  }, [formData.company])

  // Preselect vessel when vesselId is provided
  useEffect(() => {
    if (!visible || !vesselId) return

    const hydrate = async () => {
      const match = vessels.find(v => v.id === vesselId)
      if (match) {
        setFormData(prev => ({
          ...prev,
          vessel: prev.vessel || String(match.id),
        }))
        return
      }

      try {
        const vessel = await getVesselById(vesselId)
        if (vessel) {
          const scoped = filterVesselsForContext([vessel])
          setVessels(scoped)
          setFormData(prev => ({
            ...prev,
            vessel: String(vessel.id),
          }))
        }
      } catch (error) {
        console.error('UpdateInventoryModal - Error hydrating vessel by id:', error)
      }
    }

    hydrate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, vesselId, vessels])

  // Load vessels when company is selected
  useEffect(() => {
    if (formData.company) {
      console.log('UpdateInventoryModal - Company selected:', formData.company)
      console.log('UpdateInventoryModal - Available companies:', companies)
      const selectedCompany = companies.find(c => c.id === parseInt(formData.company))
      console.log('UpdateInventoryModal - Found company:', selectedCompany)
      if (selectedCompany) {
        console.log('UpdateInventoryModal - Loading vessels for username:', selectedCompany.username)
        loadVessels(selectedCompany.username)
      } else {
        console.log('UpdateInventoryModal - Company not found in companies array')
      }
    } else {
      setVessels([])
      setFormData(prev => ({ ...prev, vessel: '' }))
    }
  }, [formData.company, companies])

  // Load categories when company and vessel are selected
  useEffect(() => {
    if (formData.company && formData.vessel) {
      loadInventoryCategoriesForUpdate(parseInt(formData.company, 10), Number(formData.vessel))
    } else if (formData.vessel && !libraryScopeAll) {
      const cgaIdToUse = tenantCgaId
      if (!cgaIdToUse) {
        setInventoryCategories([])
        return
      }
      loadInventoryCategoriesForUpdate(cgaIdToUse, Number(formData.vessel))
    } else {
      setInventoryCategories([])
    }
  }, [formData.company, formData.vessel, libraryScopeAll, tenantCgaId])

  // Handle cascading dropdowns
  useEffect(() => {
    if (formData.category) {
      loadHeads()
    } else {
      setHeads([])
    }
  }, [formData.category])

  useEffect(() => {
    if (formData.head) {
      loadSubHeads()
    } else {
      setSubHeads([])
    }
  }, [formData.head])

  useEffect(() => {
    if (formData.subHead) {
      const subHeadId = toNumber(formData.subHead)
      if (subHeadId) loadPartsBySubHead(subHeadId)
    } else {
      setFilteredParts([])
    }
  }, [formData.subHead, parts])

  useEffect(() => {
    if (!isGeneralItem) return
    if (!selectedGeneralItemId || selectedGeneralItemId === 'OTHER') return
    const selected = generalItems.find((gi) => String(gi.id) === selectedGeneralItemId)
    if (!selected) {
      setSelectedGeneralItemId('')
      setFormData(prev => ({...prev, itemName: '', unit: 'PCS'}))
      return
    }
    if (formData.subHead && String(selected.inventoryItemSubHeadId) !== String(formData.subHead)) {
      setSelectedGeneralItemId('')
      setFormData(prev => ({...prev, itemName: '', unit: 'PCS'}))
    }
  }, [formData.subHead, isGeneralItem, generalItems, selectedGeneralItemId])

  useEffect(() => {
    if (formData.part) {
      loadSubComponentsByPart(parseInt(formData.part))
    } else {
      setSubComponents([])
    }
  }, [formData.part])

  useEffect(() => {
    if (visible) {
      loadEquipmentForVessel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, formData.vessel, vesselId])

  useEffect(() => {
    if (!visible || !isGeneralItem) return
    const vId = toNumber(formData.vessel) ?? vesselId
    if (!vId) {
      setGeneralItems([])
      return
    }
    const loadGeneralItems = async () => {
      setIsLoadingGeneralItems(true)
      try {
        const data = await getGeneralInventoryItems(vId)
        setGeneralItems(Array.isArray(data) ? data : [])
      } catch (error) {
        console.error('UpdateInventoryModal - Error loading general items:', error)
        setGeneralItems([])
      } finally {
        setIsLoadingGeneralItems(false)
      }
    }
    loadGeneralItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isGeneralItem, formData.vessel, vesselId])

  useEffect(() => {
    const equipmentId = toNumber(formData.machinery)
    if (equipmentId) {
      loadSubComponentsForEquipment(equipmentId)
    } else {
      setFilteredSubComponents([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.machinery])

  useEffect(() => {
    const subCompId = toNumber(formData.subComponent)
    if (subCompId) {
      getPartsBySubcomponent(subCompId)
        .then(res => {
          setFilteredParts(res || [])
          if ((res || []).length === 1) {
            const only = res![0]
            setFormData(prev => ({
              ...prev,
              part: String(only.id),
              code: only.code,
              itemName: only.name,
              partNumber: only.partNo,
              unit: only.uom?.id?.toString() || '',
              serialNumber: only.serialNumber || '',
              description: `${only.manufacturer} - ${only.name}`,
            }))
          }
        })
        .catch(err => {
          console.error('UpdateInventoryModal - Error fetching parts by subcomponent:', err)
          setFilteredParts([])
        })
    }
  }, [formData.subComponent])

  const loadInitialModalData = async () => {
    setIsLoadingData(true)
    try {
      const vesselIdToUse = toNumber(formData.vessel) ?? vesselId
      if (!vesselIdToUse) {
        setInventoryCategories([])
        return
      }

      let cgaIdToUse: number | undefined
      if (libraryScopeAll) {
        cgaIdToUse = formData.company ? parseInt(formData.company, 10) : undefined
      } else {
        cgaIdToUse = tenantCgaId
      }
      if (!cgaIdToUse) {
        setInventoryCategories([])
        return
      }

      const categoriesData = await getAllInventoryCategoriesForCompany(cgaIdToUse, vesselIdToUse)
      setInventoryCategories(categoriesData)
    } catch (error) {
      console.error('Error loading modal data:', error)
    } finally {
      setIsLoadingData(false)
    }
  }

  const loadInventoryCategoriesForUpdate = async (cgaId: number, vesselId: number) => {
    setIsLoadingData(true)
    try {
      const categoriesData = await getAllInventoryCategoriesForCompany(cgaId, vesselId)
      setInventoryCategories(categoriesData)
    } catch (error) {
      console.error('Error loading inventory categories:', error)
      setInventoryCategories([])
    } finally {
      setIsLoadingData(false)
    }
  }



  const loadHeads = async () => {
    try {
      const vesselIdToUse = toNumber(formData.vessel) ?? vesselId
      const headsData = await getInventoryItemHeads(vesselIdToUse || undefined)
      setHeads(Array.isArray(headsData) ? headsData : [headsData])
    } catch (error) {
      console.error('Error loading heads:', error)
      setHeads([])
    }
  }

  const loadSubHeads = async () => {
    try {
      const vesselIdToUse = toNumber(formData.vessel) ?? vesselId
      const headId = toNumber(formData.head)
      const subHeadsData = await getInventoryItemSubHeads(vesselIdToUse || undefined, headId || undefined)
      setSubHeads(Array.isArray(subHeadsData) ? subHeadsData : [subHeadsData])
    } catch (error) {
      console.error('Error loading sub heads:', error)
      setSubHeads([])
    }
  }

  const loadPartsBySubHead = async (subHeadId: number) => {
    try {
      const filtered = Array.isArray(parts)
        ? parts.filter(p => p.inventoryItemSubHead?.id === subHeadId)
        : []
      setFilteredParts(filtered)
    } catch (error) {
      console.error('Error loading parts by sub head:', error)
      setFilteredParts([])
    }
  }

  const loadSubComponentsByPart = async (partId: number) => {
    try {
      const allSubComponents = await getSubComponents()
      const filteredSubComponents = allSubComponents.filter(sc => sc.part?.id === partId)
      setSubComponents(filteredSubComponents)
      setFilteredSubComponents(filteredSubComponents)
    } catch (error) {
      console.error('Error loading sub components by part:', error)
      setSubComponents([])
      setFilteredSubComponents([])
    }
  }

  const loadCompanies = async () => {
    setIsLoadingDropdowns(true)
    try {
      const companiesData = await getCompanyGroupAdmins()
      setCompanies(companiesData)
    } catch (error) {
      console.error('Error loading companies:', error)
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  // Load equipment for the effective vessel (form selection or provided vesselId)
  const loadEquipmentForVessel = async () => {
    try {
      const targetVesselId = toNumber(formData.vessel) ?? vesselId
      if (!targetVesselId) {
        setEquipmentList([])
        return
      }
      const list = await getEquipmentList(targetVesselId)
      setEquipmentList(list || [])
    } catch (error) {
      console.error('UpdateInventoryModal - Error loading equipment:', error)
      setEquipmentList([])
    }
  }

  const loadSubComponentsForEquipment = async (equipmentId: number) => {
    try {
      const subc = await getSubComponentsByEquipment(equipmentId)
      setFilteredSubComponents(subc || [])
    } catch (error) {
      console.error('UpdateInventoryModal - Error loading sub components by equipment:', error)
      setFilteredSubComponents([])
    }
  }

  const loadSubCompanies = async (companyId: number) => {
    try {
      const subCompaniesData = await getSubCompanyAdmins(companyId)
      setSubCompanies(subCompaniesData)
    } catch (error) {
      console.error('Error loading sub companies:', error)
      setSubCompanies([])
    }
  }

  const loadVessels = async (companyUsername: string) => {
    setIsLoadingDropdowns(true)
    try {
      console.log('UpdateInventoryModal - Loading vessels for company:', companyUsername)
      const vesselsData = await getVesselsByCompany(companyUsername)
      console.log('UpdateInventoryModal - Vessels loaded:', vesselsData)
      const scoped = filterVesselsForContext(Array.isArray(vesselsData) ? vesselsData : [])
      setVessels(scoped)
    } catch (error) {
      console.error('UpdateInventoryModal - Error loading vessels:', error)
      setVessels([])
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    if (name === 'vessel') {
      const vesselNumber = toNumber(value)
      const match = vessels.find(v => v.id === vesselNumber)
      setFormData(prev => ({
        ...prev,
        vessel: value,
        category: '',
        head: '',
        subHead: '',
        part: '',
        subComponent: '',
        machinery: '',
      }))
      setSelectedGeneralItemId('')
      setErrors(prev => ({ ...prev, vessel: '', category: '', head: '', subHead: '', part: '' }))
      setFilteredSubComponents([])
      loadEquipmentForVessel()
      return
    }

    if (name === 'itemFor') {
      const nextIsGeneral = value === 'GENERAL_STORES'
      setFormData(prev => ({
        ...prev,
        itemFor: value,
        part: '',
        subComponent: '',
        machinery: '',
        itemName: nextIsGeneral ? prev.itemName : '',
        unit: nextIsGeneral
          ? (GENERAL_UNIT_OPTIONS.includes(prev.unit) ? prev.unit : 'PCS')
          : '',
        partNumber: nextIsGeneral ? '' : prev.partNumber,
        code: nextIsGeneral ? '' : prev.code,
        description: nextIsGeneral ? '' : prev.description,
        serialNumber: nextIsGeneral ? '' : prev.serialNumber,
      }))
      setSelectedGeneralItemId('')
      setErrors(prev => ({ ...prev, itemFor: '', part: '', itemName: '', unit: '' }))
      return
    }

    if (name === 'category') {
      setFormData(prev => ({
        ...prev,
        category: value,
        head: '',
        subHead: '',
        part: '',
        subComponent: '',
      }))
      setErrors(prev => ({ ...prev, category: '', head: '', subHead: '', part: '' }))
      return
    }

    if (name === 'head') {
      setFormData(prev => ({
        ...prev,
        head: value,
        subHead: '',
        part: '',
        subComponent: '',
      }))
      setErrors(prev => ({ ...prev, head: '', subHead: '', part: '' }))
      return
    }

    if (name === 'subHead') {
      setFormData(prev => ({
        ...prev,
        subHead: value,
        part: '',
        subComponent: '',
      }))
      setErrors(prev => ({ ...prev, subHead: '', part: '' }))
      return
    }

    if (name === 'machinery') {
      const equipmentId = toNumber(value)
      setFormData(prev => ({
        ...prev,
        machinery: value,
        subComponent: '',
      }))
      setErrors(prev => ({ ...prev, machinery: '', subComponent: '' }))
      setFilteredSubComponents([])
      if (equipmentId) loadSubComponentsForEquipment(equipmentId)
      return
    }

    // Auto-fill fields when part is selected
    if (name === 'part' && value) {
      const selectedPart = filteredParts.find(part => part.id === parseInt(value))
      if (selectedPart) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          code: selectedPart.code,
          itemName: selectedPart.name,
          partNumber: selectedPart.partNo,
          unit: selectedPart.uom?.code || selectedPart.uom?.name || '',
          serialNumber: selectedPart.serialNumber || '',
          description: `${selectedPart.manufacturer} - ${selectedPart.name}`
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }))
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleGeneralItemChange = (value: string) => {
    setSelectedGeneralItemId(value)
    if (!value) {
      setFormData(prev => ({...prev, itemName: '', unit: 'PCS'}))
      return
    }
    if (value === 'OTHER') {
      setFormData(prev => ({...prev, itemName: '', unit: 'PCS'}))
      return
    }
    const selected = generalItems.find((gi) => String(gi.id) === value)
    if (selected) {
      setFormData(prev => ({
        ...prev,
        itemName: selected.manualItemName || prev.itemName,
        unit: selected.unitOfMeasurement || prev.unit || 'PCS',
      }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.vessel) newErrors.vessel = 'Vessel is required'
    if (!formData.category) newErrors.category = 'Category is required'
    if (!formData.head) newErrors.head = 'Head is required'
    if (!formData.subHead) newErrors.subHead = 'Sub Head is required'
    if (formData.code && formData.code.length > 50) newErrors.code = 'Code must be at most 50 characters'
    if (isGeneralItem) {
      if (!formData.itemName?.trim()) newErrors.itemName = 'Item name is required'
      if (!formData.unit?.trim()) newErrors.unit = 'Unit is required'
    } else {
      if (!formData.part) newErrors.part = 'Part is required'
    }
    // Machinery/Equipment/Component optional for now
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      // Prepare the inventory creation payload
      const selectedVesselId = formData.vessel ? parseInt(formData.vessel) : vesselId
      const inventoryPayload: CreateInventoryRequest = {
        vesselId: selectedVesselId,
        itemFor: formData.itemFor === 'GENERAL_STORES' ? 'GENERAL_STORES' : 'TECHNICAL',
        partId: isGeneralItem ? null : parseInt(formData.part),
        inventoryItemSubHeadId: parseInt(formData.subHead),
        subcomponentId: isGeneralItem ? null : (formData.subComponent ? parseInt(formData.subComponent) : null),
        manualItemName: isGeneralItem ? formData.itemName.trim() : null,
        unitOfMeasurement: isGeneralItem ? formData.unit.trim() : null,
        serialNumber: isGeneralItem ? null : (formData.serialNumber || null),
        currentQty: parseInt(formData.currentQuantity),
        reorderLevel: parseInt(formData.reorderLevel),
        minThreshold: parseInt(formData.minThreshold),
        maxThreshold: parseInt(formData.maxThreshold),
        unitCost: parseFloat(formData.unitCost),
        location: formData.location || 'Main Store',
        expiryDate: formData.expiryDate || null
      }

      console.log('Creating inventory with payload:', inventoryPayload)
      console.log('Using vesselId:', selectedVesselId)

      // Call the create inventory API
      const createdItem = isGeneralItem
        ? await createGeneralInventoryItem(inventoryPayload)
        : await createInventory(inventoryPayload)
      console.log('Inventory created successfully:', createdItem)

      toast.success('Inventory updated successfully!')
      onSubmit() // This will refresh the table
      handleClose()
    } catch (error: any) {
      console.error('Error updating inventory:', error)
      toast.error('Failed to update inventory')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      company: '',
      subCompany: '',
      vessel: '',
      itemFor: 'TECHNICAL',
      category: '',
      head: '',
      subHead: '',
      machinery: '',
      part: '',
      subComponent: '',
      itemName: '',
      partNumber: '',
      code: '',
      description: '',
      unit: '',
      currentQuantity: '',
      reorderLevel: '',
      minThreshold: '',
      maxThreshold: '',
      unitCost: '',
      location: '',
      expiryDate: '',
      serialNumber: ''
    })
    setErrors({})
    // Reset API data states
    setInventoryCategories([])
    setHeads([])
    setSubHeads([])
    setFilteredParts([])
    setSubComponents([])
    setEquipmentList([])
    setFilteredSubComponents([])
    // Reset dropdown data
    setCompanies([])
    setSubCompanies([])
    setVessels([])
    setGeneralItems([])
    setSelectedGeneralItemId('')
    onClose()
  }

  if (!visible) return null

  return (
    <div
      className="modal fade show d-flex align-items-center justify-content-center"
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable' role='document'>
        <div className='modal-content bg-white' style={{ color: '#181C32' }}>
          <div className='modal-header'>
            <h5 className='modal-title'>Update Inventory</h5>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>

          <div className='modal-body' style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div className='row g-3'>

              {/* Company dropdown - only for superadmin */}
              {libraryScopeAll && (
                <div className='col-md-6'>
                  <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Company</label>
                  <SearchableSelect
                    className={`form-select ${errors.company ? 'is-invalid' : ''}`}
                    name='company'
                    value={formData.company}
                    onChange={handleInputChange}
                    disabled={isLoadingDropdowns || isLoadingData}
                  >
                    <option value=''>Select Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </SearchableSelect>
                  {errors.company && <div className='invalid-feedback'>{errors.company}</div>}
                </div>
              )}

              {/* Subcompany dropdown - only for superadmin when company is selected */}
              {libraryScopeAll && formData.company && (
                <div className='col-md-6'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Company</label>
                  <SearchableSelect
                    className='form-select'
                    name='subCompany'
                    value={formData.subCompany}
                    onChange={handleInputChange}
                    disabled={isLoadingData}
                  >
                    <option value=''>Select Sub Company (Optional)</option>
                    {subCompanies.map(subCompany => (
                      <option key={subCompany.id} value={subCompany.id}>{subCompany.name}</option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Vessel</label>
                <SearchableSelect
                  className={`form-select ${errors.vessel ? 'is-invalid' : ''}`}
                  name='vessel'
                  value={formData.vessel}
                  onChange={handleInputChange}
                  disabled={isLoadingDropdowns || (libraryScopeAll && !formData.company)}
                >
                  <option value=''>Select Vessel</option>
                  {libraryScopeAll ? (
                    formData.company ? (
                      vessels.map(vessel => (
                        <option key={vessel.id} value={vessel.id}>
                          {vessel.fleet_name}
                        </option>
                      ))
                    ) : (
                      <option disabled>Please select a company first</option>
                    )
                  ) : (
                    vessels.length > 0 ? (
                      vessels.map(vessel => (
                        <option key={vessel.id} value={vessel.id}>
                          {vessel.fleet_name}
                        </option>
                      ))
                    ) : (
                      <option disabled>No vessels found</option>
                    )
                  )}
                </SearchableSelect>
                {errors.vessel && <div className='invalid-feedback'>{errors.vessel}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Item For</label>
                <SearchableSelect
                  className='form-select'
                  name='itemFor'
                  value={formData.itemFor}
                  onChange={handleInputChange}
                  disabled={isLoadingData}
                >
                  <option value='TECHNICAL'>Technical</option>
                  <option value='GENERAL_STORES'>General Stores</option>
                </SearchableSelect>
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Category</label>
                <SearchableSelect
                  className={`form-select ${errors.category ? 'is-invalid' : ''}`}
                  name='category'
                  value={formData.category}
                  onChange={handleInputChange}
                  disabled={isLoadingData}
                >
                  <option value=''>Select Category</option>
                  {inventoryCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </SearchableSelect>
                {errors.category && <div className='invalid-feedback'>{errors.category}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Head</label>
                <SearchableSelect
                  className={`form-select ${errors.head ? 'is-invalid' : ''}`}
                  name='head'
                  value={formData.head}
                  onChange={handleInputChange}
                  disabled={!formData.category || isLoadingData}
                >
                  <option value=''>Select Head</option>
                  {heads.map(head => (
                    <option key={head.id} value={head.id}>{head.name}</option>
                  ))}
                </SearchableSelect>
                {errors.head && <div className='invalid-feedback'>{errors.head}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Head</label>
                <SearchableSelect
                  className={`form-select ${errors.subHead ? 'is-invalid' : ''}`}
                  name='subHead'
                  value={formData.subHead}
                  onChange={handleInputChange}
                  disabled={!formData.head || isLoadingData}
                >
                  <option value=''>Select Sub Head</option>
                  {subHeads.map(subHead => (
                    <option key={subHead.id} value={subHead.id}>{subHead.name}</option>
                  ))}
                </SearchableSelect>
                {errors.subHead && <div className='invalid-feedback'>{errors.subHead}</div>}
              </div>

              {isGeneralItem && (
                <div className='col-md-6'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>General Item</label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedGeneralItemId}
                    onChange={(e) => handleGeneralItemChange(e.target.value)}
                    disabled={isLoadingGeneralItems || !formData.subHead}
                  >
                    <option value=''>Select General Item</option>
                    {(generalItems || [])
                      .filter((gi) =>
                        formData.subHead
                          ? String(gi.inventoryItemSubHeadId) === String(formData.subHead)
                          : true
                      )
                      .map((gi) => (
                        <option key={gi.id} value={String(gi.id)}>
                          {gi.manualItemName}
                        </option>
                      ))}
                    <option value='OTHER'>Other (Add new)</option>
                  </SearchableSelect>
                </div>
              )}

              <div className='col-12'>
                <div className='row g-3'>
                  <div className='col-md-3'>
                    <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Machinery</label>
                    <SearchableSelect
                      className='form-select'
                      name='machinery'
                      value={formData.machinery}
                      onChange={handleInputChange}
                      disabled={isLoadingData || isGeneralItem}
                    >
                      <option value=''>Select Machinery</option>
                      {machineryOptions.map(opt => (
                        <option key={opt.id} value={opt.id}>{opt.name || opt.code || `Equipment #${opt.id}`}</option>
                      ))}
                    </SearchableSelect>
                  </div>

                  <div className='col-md-3'>
                    <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Component</label>
                    <SearchableSelect
                      className='form-select'
                      name='subComponent'
                      value={formData.subComponent}
                      onChange={handleInputChange}
                      disabled={isGeneralItem || !formData.machinery || isLoadingData}
                    >
                      <option value=''>Select Sub Component (Optional)</option>
                      {(filteredSubComponents.length ? filteredSubComponents : subComponents).map(subComp => (
                        <option key={subComp.id} value={subComp.id}>{subComp.name}</option>
                      ))}
                    </SearchableSelect>
                  </div>
                </div>
              </div>

              <div className='col-md-6'>
                <label className={`form-label fw-semibold fs-6 mb-2 ${!isGeneralItem ? 'required' : ''}`} style={{ color: '#181C32' }}>Part</label>
                <SearchableSelect
                  className={`form-select ${errors.part ? 'is-invalid' : ''}`}
                  name='part'
                  value={formData.part}
                  onChange={handleInputChange}
                  disabled={isGeneralItem || (!formData.subHead && !formData.subComponent) || isLoadingData}
                >
                  <option value=''>Select Part</option>
                  {filteredParts.map(part => (
                    <option key={part.id} value={part.id}>{part.name}</option>
                  ))}
                </SearchableSelect>
                {errors.part && <div className='invalid-feedback'>{errors.part}</div>}
              </div>

              {/* Rest of the fields from Add Item Modal (excluding category and location) */}
              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Item Code</label>
                <input
                  type='text'
                  className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                  name='code'
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder='e.g., FLT-1001'
                  style={{ color: '#000' }}
                  maxLength={50}
                />
                {errors.code && <div className='invalid-feedback'>{errors.code}</div>}
              </div>

              <div className='col-md-6'>
                <label className={`form-label fw-semibold fs-6 mb-2 ${isGeneralItem ? 'required' : ''}`} style={{ color: '#181C32' }}>Item Name</label>
                <input
                  type='text'
                  className={`form-control ${errors.itemName ? 'is-invalid' : ''}`}
                  name='itemName'
                  value={formData.itemName}
                  onChange={handleInputChange}
                  placeholder='e.g., Fuel Filter'
                  style={{ color: '#000' }}
                  disabled={isGeneralItemExisting}
                />
                {errors.itemName && <div className='invalid-feedback d-block'>{errors.itemName}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Part Number</label>
                <input
                  type='text'
                  className='form-control'
                  name='partNumber'
                  value={formData.partNumber}
                  onChange={handleInputChange}
                  placeholder='e.g., FLT-1245'
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Description</label>
                <input
                  type='text'
                  className='form-control'
                  name='description'
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder='Brief description of the item'
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className={`form-label fw-semibold fs-6 mb-2 ${isGeneralItem ? 'required' : ''}`} style={{ color: '#181C32' }}>Unit of Measure</label>
                <SearchableSelect
                  className={`form-select ${errors.unit ? 'is-invalid' : ''}`}
                  name='unit'
                  value={formData.unit}
                  onChange={handleInputChange}
                  disabled={!isGeneralItem || isGeneralItemExisting}
                >
                  <option value=''>Select Unit</option>
                  <option value='PCS'>Pieces</option>
                  <option value='SET'>Sets</option>
                  <option value='LTR'>Liters</option>
                  <option value='KG'>Kilograms</option>
                  <option value='MTR'>Meters</option>
                  <option value='BOX'>Box</option>
                  <option value='PKT'>Packet</option>
                  <option value='PAIR'>Pair</option>
                </SearchableSelect>
                {errors.unit && <div className='invalid-feedback'>{errors.unit}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Current Quantity</label>
                <input
                  type='number'
                  className='form-control'
                  name='currentQuantity'
                  value={formData.currentQuantity}
                  onChange={handleInputChange}
                  placeholder='0'
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Reorder Level</label>
                <input
                  type='number'
                  className='form-control'
                  name='reorderLevel'
                  value={formData.reorderLevel}
                  onChange={handleInputChange}
                  placeholder='0'
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Min Threshold</label>
                <input
                  type='number'
                  className='form-control'
                  name='minThreshold'
                  value={formData.minThreshold}
                  onChange={handleInputChange}
                  placeholder='0'
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Max Threshold</label>
                <input
                  type='number'
                  className='form-control'
                  name='maxThreshold'
                  value={formData.maxThreshold}
                  onChange={handleInputChange}
                  placeholder='0'
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Unit Cost ($)</label>
                <input
                  type='number'
                  step='0.01'
                  className='form-control'
                  name='unitCost'
                  value={formData.unitCost}
                  onChange={handleInputChange}
                  placeholder='0.00'
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Location</label>
                <input
                  type='text'
                  className='form-control'
                  name='location'
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder='e.g., ER RACK A2'
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Expiry Date</label>
                <input
                  type='date'
                  className='form-control'
                  name='expiryDate'
                  value={formData.expiryDate}
                  onChange={handleInputChange}
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Serial Number</label>
                <input
                  type='text'
                  className='form-control'
                  name='serialNumber'
                  value={formData.serialNumber}
                  onChange={handleInputChange}
                  placeholder='e.g., SN001'
                  style={{ color: '#000' }}
                />
              </div>
            </div>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn_primary'
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Inventory'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UpdateInventoryModal;
